/**
 * sl-apue + sl-oee3: SeatAddonSection — Shows seat addon as a pricing card.
 *
 * Uses shared pricing card styles for visual consistency with plan cards.
 * Two states: no addon yet (buy CTA) or active addon (manage/remove).
 * Only visible when the plan supports extra seats (canPurchaseSeats).
 *
 * Pricing display adapts to the workspace billing interval (monthly/yearly)
 * since seat addons inherit the main subscription's interval.
 */
import { Button, notify, useConfirmModal } from '@affine/component';
import {
  SeatAddonService,
  WorkspaceSubscriptionService,
} from '@affine/core/modules/cloud';
import { formatPrice } from '@affine/core/utils/format-price';
import {
  getStartingPrice,
  parseSeatTiers,
} from '@affine/core/utils/graduated-pricing';
import { useI18n } from '@affine/i18n';
import { DoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as styles from '../../../../../components/pricing/pricing-cards.css';
import { SeatManagerDialog } from './seat-manager-dialog';

export const SeatAddonSection = () => {
  const t = useI18n();
  const seatAddonService = useService(SeatAddonService);
  const wsSubService = useService(WorkspaceSubscriptionService);

  const addon = useLiveData(seatAddonService.seatAddon.addon$);
  const hasExtraSeats = useLiveData(seatAddonService.seatAddon.hasExtraSeats$);
  const canPurchaseSeats = useLiveData(
    seatAddonService.seatAddon.canPurchaseSeats$
  );
  const memberQuota = useLiveData(seatAddonService.seatAddon.memberQuota$);
  const wsSub = useLiveData(wsSubService.subscription.subscription$);
  const isFreePlan = !wsSub || wsSub.plan === 'Free';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const { openConfirmModal } = useConfirmModal();

  const locale = t.language?.startsWith('en') ? 'en-US' : 'de-DE';
  // sl-fwcn: Addons always follow the plan interval — no separate addon interval.
  const isYearly = wsSub?.recurring === 'Yearly';

  useEffect(() => {
    seatAddonService.seatAddon.revalidate();
  }, [seatAddonService]);

  const seatTiers = useMemo(
    () => parseSeatTiers(memberQuota?.seatTiers),
    [memberQuota?.seatTiers]
  );

  const startingPrice = useMemo(() => {
    const tiers = isYearly ? seatTiers?.yearly : seatTiers?.monthly;
    return tiers ? getStartingPrice(tiers) : 0;
  }, [seatTiers, isYearly]);

  const handleOpenManager = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleRemoveSeats = useCallback(() => {
    openConfirmModal({
      title: t['com.streamlain.payment.seat-addon.remove.confirm.title'](),
      description:
        t['com.streamlain.payment.seat-addon.remove.confirm.description'](),
      confirmText: t['com.streamlain.payment.seat-addon.remove.confirm'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: async () => {
        setIsRemoving(true);
        try {
          await seatAddonService.seatAddon.removeSeats();
          notify.success({
            title: t['com.streamlain.payment.seat-addon.remove.success'](),
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          notify.error({
            title: t['com.streamlain.payment.seat-addon.error'](),
            message,
          });
          console.error('Failed to remove seat addon:', err);
        } finally {
          setIsRemoving(false);
        }
      },
    });
  }, [openConfirmModal, seatAddonService, t]);

  // The quota payload is pricing metadata, not an authorization decision.
  // Keep the UI aligned with the backend guard: Free workspaces cannot buy
  // seat addons even if cached/stale quota metadata still contains tiers.
  if (!canPurchaseSeats || isFreePlan) {
    return null;
  }

  const activeAddon = hasExtraSeats && addon ? addon : null;
  const isPlanExpiring = Boolean(wsSub?.canceledAt);
  const currentQuantity = activeAddon?.quantity ?? 0;
  const totalCents = isYearly
    ? (activeAddon?.totalYearly ?? 0)
    : (activeAddon?.totalMonthly ?? 0);
  const isActive = Boolean(activeAddon);
  const intervalKey = isYearly
    ? 'com.streamlain.payment.pricing.per-year'
    : 'com.streamlain.payment.pricing.per-month';

  const features = [
    t['com.streamlain.payment.addon.seats.feature-flexible'](),
    t['com.streamlain.payment.addon.seats.feature-graduated'](),
    t['com.streamlain.payment.addon.seats.feature-prorated'](),
  ];

  return (
    <div data-testid="addon-section-seats" className={styles.addonSection}>
      <div
        data-testid="addon-section-title"
        className={styles.addonSectionTitle}
      >
        {t['com.streamlain.payment.seat-addon.title']()}
      </div>

      <div className={styles.pricingCardsGrid}>
        <div className={styles.pricingCard}>
          {isActive && (
            <div className={styles.pricingCurrentBadge}>
              {t['com.streamlain.payment.addon.seats.active-badge']({
                count: String(activeAddon?.quantity ?? 0),
              })}
            </div>
          )}

          <div className={styles.pricingCardHeader}>
            <div className={styles.pricingCardName}>
              {t['com.streamlain.payment.seat-addon.title']()}
            </div>
            <div className={styles.pricingCardDescription}>
              {isActive
                ? t['com.streamlain.payment.seat-addon.description']({
                    count: String(activeAddon?.quantity ?? 0),
                    price: formatPrice(totalCents, 'eur', locale),
                  })
                : t['com.streamlain.payment.seat-addon.description-none']()}
            </div>
          </div>

          <div className={styles.pricingCardPriceSection}>
            {isActive ? (
              <div className={styles.pricingCardPrice}>
                {formatPrice(totalCents, 'eur', locale)}
                <span className={styles.pricingCardPriceInterval}>
                  {t[intervalKey]()}
                </span>
              </div>
            ) : startingPrice > 0 ? (
              <div className={styles.pricingCardPrice}>
                {t['com.streamlain.payment.addon.seats.from-price']({
                  price: formatPrice(startingPrice, 'eur', locale),
                })}
                <span className={styles.pricingCardPriceInterval}>
                  {t[intervalKey]()}
                </span>
              </div>
            ) : null}
          </div>

          {isActive ? (
            <>
              {!isPlanExpiring ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    variant="primary"
                    style={{ flex: 1 }}
                    onClick={handleOpenManager}
                  >
                    {t['com.streamlain.payment.seat-addon.manage']()}
                  </Button>
                  <Button
                    variant="secondary"
                    style={{ flex: 1 }}
                    onClick={handleRemoveSeats}
                    loading={isRemoving}
                  >
                    {t['com.streamlain.payment.seat-addon.remove']()}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <button
              className={styles.pricingCardCta}
              data-featured="true"
              onClick={handleOpenManager}
            >
              {t['com.streamlain.payment.seat-addon.buy']()}
            </button>
          )}

          <ul className={styles.pricingCardFeatures}>
            {features.map((feature, i) => (
              <li key={i} className={styles.pricingCardFeatureItem}>
                <DoneIcon className={styles.pricingCardFeatureIcon} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <SeatManagerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentQuantity={currentQuantity}
      />
    </div>
  );
};
